import React, {useEffect, useRef, useState} from 'react'
import {get} from 'lodash'
import {MESSAGE_TYPE_IMAGE, PENDING_MESSAGE_ID} from 'core/constants'
import Icon from './Icon'
import useOptions from 'core/hooks/useOptions'
import {T} from './Translation'
import {observer} from 'mobx-react'
import useEventListener from 'core/hooks/useEventListener'

const ERRORS = {
  entityTooLarge: 'errorEntityTooLarge',
  allowedImageTypes: 'errorAllowedImageTypes',
  default: 'errorDefault',
}

const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
]

const MAX_FILE_SIZE = 10485760

export const FileUploadButton = observer(({ user, conversation, label = '', classNames = '' }) => {
  const [{ features, hooks }] = useOptions()

  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const imageUploadMessage = (user.premium || features.hasFreeCredits) && features.isCreditSite ? features.creditsForSendingPhotos : null

  useEffect(() => {
    const pendingImage = !!conversation.messages.find(m => m.id === PENDING_MESSAGE_ID && m.type === MESSAGE_TYPE_IMAGE)
    setLoading(pendingImage)
  }, [conversation.messages.length])

  useEffect(() => {
    if (user.premium) {
      conversation.removeTempMessage()
    }
  }, [user.premium])

  useEventListener('click', () => {
    setError(null)
  }, [], document.querySelector('.chat-widget'))

  useEffect(() => {
    setError(null)
  }, [user.premium, conversation?.conversationUid])

  if (!features.canUploadImages) {
    return null
  }

  const input = useRef(null)

  const handleClick = e => {
    const { onMessageSend } = hooks
    e.preventDefault()

    if (error) {
      setError(false)
    }

    if (!user.premium && (!features.isCreditSite || (features.isCreditSite && !features.hasFreeCredits))) {
      conversation.addTempMessage({
        hideOnClick: ['.chat-item-picker-toggle', '.upload-button-container', '.chat-input', '.chat-input-button', '.sex-icon-wrapper'],
        title: 'warning',
        message: features.isCreditSite ? 'imageUpload.premiumCredits' : 'imageUpload.premium',
      })

      onMessageSend({ showPurchaseModal: true })
    } else {
      input.current.click()
    }
  }

  const handleOnFilesChange = e => {
    setError(null)

    const { files } = e.target
    if (!files.length) {
      setLoading(false)
      return
    }

    const file = files[0]

    //validate the file
    const hasError = file.size > MAX_FILE_SIZE ? 'entityTooLarge' : !ALLOWED_FILE_TYPES.includes(file.type) ? 'allowedImageTypes' : null
    if (!hasError) {
      setLoading(true)
      conversation.uploadImage(file)
        .then(([, err]) => {
          if (err) {
            setLoading(false)
            setError(err)
          }
          setLoading(false)
        })
    } else {
      setError(hasError)
    }
    e.target.value = ''
  }

  return (
    <div className={`upload-button-container chat-item-picker chat-item-picker-closed ${loading ? 'pending' : ''} ${classNames}`}>
      <div className={`chat-item-picker-toggle chat-item-picker-toggle ${imageUploadMessage != null ? 'custom-tooltip' : '' }`} role="button"
           onClick={handleClick}>
        <Icon iconName="images"/>{label}
        <div className="credit-cost-tooltip-text">{imageUploadMessage}</div>
      </div>
      <input type="file" ref={input} onChange={handleOnFilesChange} disabled={loading} multiple={false}
             accept=".jpeg,.jpg,.png"/>
      {error && <p className="upload-button_error"><T>{`imageUpload.${get(ERRORS, error, ERRORS.default)}`}</T></p>}
    </div>
  )
})
